import Box from '@mui/material/Box';
import { GridCellParams, GridColDef } from '@mui/x-data-grid-pro';
import { useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import { useParams } from 'react-router-dom';

import { useGetProjectQuery } from '@verantos-dev/data-access-projects';
import { DataGridColumnHeaderWithToolTip } from '@verantos-dev/shared-components';

import { dataASINC, studyASINC, studyINSIGHT, dataINSIGHT } from '../data/data';
import type { AccuracyConcept } from '../types/types';

export const useAccuracyConceptList = () => {
  const intl = useIntl();
  const { projectId } = useParams();
  const { data: projectData } = useGetProjectQuery(projectId || '', { skip: !projectId });
  const projectName = projectData?.data.projectName;
  const [openConfusionMatrixDialog, setOpenConfusionMatrixDialog] = useState(false);
  const [selectedConcept, setSelectedConcept] = useState<AccuracyConcept | null>(null);

  const gridMessages = {
    noRows: intl.formatMessage({ id: 'dataReliability.noRows' }),
  };

  const onConceptClick = (concept: AccuracyConcept) => {
    setSelectedConcept(concept);
    setOpenConfusionMatrixDialog(true);
  };

  const useFetchDataGridData = () => {
    return [];
  };

  const gridData: AccuracyConcept[] = useMemo(() => {
    if (studyASINC.some((study) => study === projectName)) {
      return dataASINC;
    }

    if (studyINSIGHT.some((study) => study === projectName)) {
      return dataINSIGHT;
    }

    return [];
  }, [projectName]);

  const accuracyTableColumns: GridColDef[] = [
    {
      field: 'conceptName',
      headerName: intl.formatMessage({ id: 'dataReliability.columnConcept' }),
      flex: 1,
      type: 'string',
      minWidth: 306,
      filterable: false,
      sortable: false,
      renderCell: (params: GridCellParams) => {
        return (
          <Box
            sx={{
              color: '#01579b',
              cursor: 'pointer',
            }}
            onClick={() => onConceptClick(params.row)}
          >
            {params.row.conceptName}
          </Box>
        );
      },
    },
    {
      field: 'recall',
      headerName: intl.formatMessage({ id: 'dataReliability.columnRecall' }),
      flex: 1,
      renderHeader: () => (
        <DataGridColumnHeaderWithToolTip
          id="accuracy-table-recall"
          headerName={intl.formatMessage({ id: 'dataReliability.columnRecall' })}
          description={intl.formatMessage({ id: 'dataReliability.columnRecall.tooltip' })}
          placement="left"
        />
      ),
    },
    {
      field: 'precision',
      headerName: intl.formatMessage({ id: 'dataReliability.columnPrecision' }),
      flex: 1,
      renderHeader: () => (
        <DataGridColumnHeaderWithToolTip
          id="accuracy-table-precision"
          headerName={intl.formatMessage({ id: 'dataReliability.columnPrecision' })}
          description={intl.formatMessage({ id: 'dataReliability.columnPrecision.tooltip' })}
          placement="left"
        />
      ),
    },
    {
      field: 'fMeasure',
      headerName: intl.formatMessage({ id: 'dataReliability.columnFMeasure' }),
      flex: 1,
      renderHeader: () => (
        <DataGridColumnHeaderWithToolTip
          id="accuracy-table-fmeasure"
          headerName={intl.formatMessage({ id: 'dataReliability.columnFMeasure' })}
          description={intl.formatMessage({ id: 'dataReliability.columnFMeasure.tooltip' })}
          placement="left"
        />
      ),
    },
  ];

  return {
    accuracyTableColumns,
    gridData,
    openConfusionMatrixDialog,
    setOpenConfusionMatrixDialog,
    selectedConcept,
    setSelectedConcept,
    useFetchDataGridData,
    gridMessages,
  };
};
